import { get, includes, difference, map, defaultTo } from "lodash";
import type { RootState } from "types/state";
import type { UserBank } from "types/models";

export const isBankVerified = (bank?: UserBank | null) => {
  const verifiedStates = ["BankAccountActive", "BankAccountPendingAchLink"];
  return includes(verifiedStates, get(bank, "state"));
};

export const isPendingManualBankVerification = (bank?: UserBank | null) => {
  return get(bank, "state") === "BankAccountPendingManualVerification";
};

export const isPendingAutoBankVerification = (bank?: UserBank | null) => {
  return get(bank, "state") === "BankAccountPendingAutomaticVerification";
};

export const isBankPending = (bank?: UserBank | null) => {
  return (
    isPendingManualBankVerification(bank) ||
    isPendingAutoBankVerification(bank) ||
    get(bank, "state") === "BankAccountVerified"
  );
};

export const isBankAwaitingAchLink = (bank?: UserBank | null) => {
  const awaitingStates = ["BankAccountCreated", "BankAccountVerified"];
  return includes(awaitingStates, get(bank, "state"));
};

export const banksSelector = (state: RootState): UserBank[] => {
  return get(state, "accounts.bankAccounts");
};

export const activeBanksSelector = (state: RootState): UserBank[] => {
  return get(state, "accounts.bankAccounts").filter((account) =>
    isBankVerified(account)
  );
};

export const activeBankSelector = (state: RootState) => {
  return get(activeBanksSelector(state), "[0]");
};

export const bankSelector = (state: RootState) => {
  return defaultTo(activeBankSelector(state), get(banksSelector(state), "[0]"));
};

export const selectableBanksSelector = (state: RootState): UserBank[] => {
  return get(state, "accounts.bankAccounts").filter((account) => {
    return (
      isBankVerified(account) || get(account, "state") === "BankAccountVerified"
    );
  });
};

export const pendingBanksSelector = (state: RootState): UserBank[] => {
  return get(state, "accounts.bankAccounts").filter((account) =>
    isBankPending(account)
  );
};

export const isBankVerifiedSelector = (state: RootState) => {
  return isBankVerified(bankSelector(state));
};

export const isBankAwaitingAchLinkSelector = (state: RootState) => {
  return isBankAwaitingAchLink(bankSelector(state));
};

export const isPendingManualBankVerificationSelector = (state: RootState) => {
  return isPendingManualBankVerification(bankSelector(state));
};

export const isPendingAutoBankVerificationSelector = (state: RootState) => {
  return isPendingAutoBankVerification(bankSelector(state));
};

export const invalidBankIdsLinkedToGroupsSelector = (state: RootState) => {
  const associatedBankAccountIds: unknown[] = map(
    state.employer.groups,
    "associatedBankAccount.id"
  );
  const activeBankIds = map(activeBanksSelector(state), "id");
  const pendingBankIds = map(pendingBanksSelector(state), "id");
  return difference(associatedBankAccountIds, [
    ...activeBankIds,
    ...pendingBankIds,
  ]);
};
